import { dayjs } from '@/core/config';
import getAccountTypeLabel from '@/helpers/getAccountTypeLabel';
import { currencyConfigFormat } from '@/helpers/formatCurrency';
import { checkIfBeneficiaryHasSwiftRoute } from '@/helpers/swift';
import { getRateBreakdown } from '@/shared/common/utils/rateBreakdown.util';

import type { Transaction } from '@/types/TransactionType';
import type {
  CurrencyCloudVoucher,
  ExchangeVoucher,
  InvoiceVoucher,
  P2pVoucher,
  PseVoucher,
  StablecoinVoucher,
  StablecoinPayoutVoucher,
} from '@/types/InvoiceVoucher';
import { getStablecoinStatus } from './stablecoinStatus.util';
import { transferStatus } from '@/shared/common/utils/transactionModalData.util';
import type { IExchangeDetail } from '@/types/Exchange';
import type { IRemittanceDetailResponse } from '@/shared/common/types/remittance.type';
import type { NewTypeMovement } from '@/shared/common/types/movement.type';
import type { DepositDetail } from '../types/deposit.type';
import type { CountryData } from '@/types/CountryType';
import type { KycCompliance } from '@/types/KycCompliance';
import type { Destinatary } from '@/types/DestinataryType';
import type PaymentType from '@/types/PaymentTypes';

function formatMovementDate(dateMillis: number): string {
  const date = dayjs(dateMillis);
  const day = date?.format('DD') || null;
  const month = date?.format('MMMM') || null;
  const year = date?.format('YYYY') || null;
  return `${day || '01'} de ${month || 'Enero'} ${year || '1970'}`;
}

export function buildDefaultInvoicePayload(
  transactionData: Transaction | IRemittanceDetailResponse
): InvoiceVoucher {
  const operationCostAmount =
    transactionData?.transactionCostList?.find(
      (cost) => cost.chargeType === 'COSTO OPERACIONAL'
    )?.amount || 0;

  const isSwift: boolean = checkIfBeneficiaryHasSwiftRoute(
    transactionData.beneficiary as Destinatary
  );

  let operationCost = currencyConfigFormat({
    amount: operationCostAmount,
    currencyCode: transactionData.originCurrency,
    hasCode: true,
  });

  let fee = currencyConfigFormat({
    amount: transactionData?.fee || 0,
    currencyCode: transactionData.originCurrency,
    hasCode: true,
  });

  const rateBreakdown = getRateBreakdown({
    destinationAmount: transactionData.destinyAmount,
    destinationCurrency: transactionData.destinyCurrency,
    destinyRate: transactionData.destinyRate,
    fee: transactionData.fee,
    originAmount: transactionData.originAmount,
    originCurrency: transactionData.originCurrency,
    rate: transactionData.rate,
    destinyRateString: transactionData.destinyRateString,
  });
  const global66TypeChange =
    rateBreakdown.global66Formatted ||
    transactionData?.destinyRateString ||
    null;

  if (isSwift) {
    const feeAmount = operationCostAmount + transactionData?.fee;
    operationCost = currencyConfigFormat({
      amount: 0,
      currencyCode: transactionData.originCurrency,
      hasCode: true,
    });
    fee = currencyConfigFormat({
      amount: feeAmount || 0,
      currencyCode: transactionData.originCurrency,
      hasCode: true,
    });
  }

  return {
    transactionId: String(transactionData.transactionId) || '',
    destinyAlphaCode: transactionData.destinyAlphaCode,
    originAlphaCode: transactionData.originAlphaCode,
    startDateDay: transactionData?.startDateMillis
      ? dayjs(transactionData.startDateMillis).format('D')
      : null,
    startDateMonth: transactionData?.startDateMillis
      ? dayjs(transactionData.startDateMillis).format('MMMM')
      : null,
    successDateDay: transactionData?.successDateMillis
      ? dayjs(transactionData.successDateMillis).format('D')
      : null,
    successDateMonth: transactionData?.successDateMillis
      ? dayjs(transactionData.successDateMillis).format('MMMM')
      : null,
    slaDeadlineDateDay: transactionData?.slaDeadlineDateMillis
      ? Number(dayjs(transactionData.slaDeadlineDateMillis).format('D'))
      : null,
    slaDeadlineDateMonth: transactionData?.slaDeadlineDateMillis
      ? dayjs(transactionData.slaDeadlineDateMillis).format('MMMM')
      : null,
    statusDB: transactionData?.txStatus?.statusDB || null,
    paymentMethod: (transactionData?.paymentMethod as PaymentType) || null,
    remitterName: transactionData?.remitter?.name || null,
    remitterContactNumber: transactionData?.remitter?.contactNumber || null,
    customerEmail: transactionData?.customerEmail || null,
    beneficiaryName: transactionData?.beneficiary?.name || null,
    beneficiaryEmail: transactionData?.beneficiary?.email || null,
    beneficiaryAccountBankName:
      transactionData?.beneficiary?.accountBankName || null,
    beneficiaryAccountType: getAccountTypeLabel(
      transactionData?.beneficiary?.accountType || ''
    ),
    beneficiaryAccountNumber:
      transactionData?.beneficiary?.accountNumber || null,
    beneficiaryCountryName: transactionData?.beneficiary?.countryName || null,
    originAmount: currencyConfigFormat({
      amount:
        transactionData?.originAmount + transactionData?.discountAmount || 0,
      currencyCode: transactionData.originCurrency,
      hasCode: true,
    }),
    fee,
    destinyRate: transactionData?.destinyRateString || null,
    global66TypeChange,
    spotRate: rateBreakdown.showBreakdown
      ? rateBreakdown.spotFormatted || null
      : null,
    spreadRate: rateBreakdown.showBreakdown
      ? rateBreakdown.spreadFormatted || null
      : null,
    webpayOriginDiff: transactionData?.webpayOriginDiff
      ? currencyConfigFormat({
          amount: transactionData?.webpayOriginDiff || 0,
          currencyCode: transactionData.originCurrency,
          hasCode: true,
        })
      : null,
    discountAmount: currencyConfigFormat({
      amount: transactionData?.discountAmount || 0,
      currencyCode: transactionData.originCurrency,
      hasCode: true,
    }),
    destinyAmount: currencyConfigFormat({
      amount: transactionData?.destinyAmount || 0,
      currencyCode: transactionData.destinyCurrency,
      hasCode: true,
    }),
    purpose: transactionData?.purpose || null,
    comment: transactionData?.comment || null,
    isCashWithdrawal: false,
    operationCost,
  };
}

/**
 * Adapta la respuesta de getP2pRemittance (endpoint p2p-remittances) al contrato
 * InvoiceVoucher. Ese endpoint entrega los códigos de país con otro nombre
 * (beneficiary.countryCode / originCountry) que buildDefaultInvoicePayload no lee,
 * por eso salían en null. El resto de campos aún ausentes en la respuesta
 * (customerEmail, remitter.contactNumber, paymentMethod, desglose de tasa) quedan en
 * null hasta que el backend los incluya; cuando lleguen, fluyen sin cambios acá.
 */
export function buildP2pRemittanceInvoicePayload(
  transactionData: IRemittanceDetailResponse
): InvoiceVoucher {
  return buildDefaultInvoicePayload({
    ...transactionData,
    destinyAlphaCode:
      transactionData.destinyAlphaCode ||
      transactionData.beneficiary?.countryCode ||
      '',
    originAlphaCode:
      transactionData.originAlphaCode || transactionData.originCountry || '',
  });
}

export function buildDepositCurrencyCloudTemplateValues({
  movement,
  depositDetail,
  kycStatus,
  costRep,
  countries,
}: {
  movement: NewTypeMovement;
  depositDetail: DepositDetail;
  kycStatus?: KycCompliance;
  countryRep?: string;
  costRep?: string;
  countries?: CountryData[];
}): CurrencyCloudVoucher {
  const dateMillis =
    depositDetail?.paidDateMillis ||
    depositDetail?.successPartnerDateMillis ||
    depositDetail?.createdDateMillis ||
    movement?.date ||
    0;
  const formattedDate = formatMovementDate(dateMillis);

  const amount = depositDetail?.amount ?? movement?.amount ?? 0;
  const currencyCode = depositDetail?.currency || movement?.originCurrency;

  const txCosts = depositDetail?.transactionCostList ?? [];
  const { totalTxCost, hasNoCost } = txCosts.reduce(
    (
      acc: { totalTxCost: number; hasNoCost: boolean },
      item: { amount?: number; chargeType?: string } | null | undefined
    ) => {
      const itemAmount = item?.amount ?? 0;
      const isNoCost = (item?.chargeType ?? '').toUpperCase() === 'SIN COSTO';
      return {
        totalTxCost: acc.totalTxCost + itemAmount,
        hasNoCost: acc.hasNoCost || isNoCost,
      };
    },
    { totalTxCost: 0, hasNoCost: false }
  );

  const computedCost =
    hasNoCost || !currencyCode || totalTxCost <= 0
      ? 'Sin costo'
      : currencyConfigFormat({ amount: totalTxCost, currencyCode, hasCode: true });

  const country =
    countries?.find(
      (c: CountryData) =>
        c.alpha2Code === kycStatus?.company?.addressCountry?.country
    ) || ({} as CountryData);

  const bankTx = depositDetail?.bankAccountTransaction;
  const thirdParty = depositDetail?.thirdPartyClient;

  const remitterName =
    thirdParty?.name ||
    bankTx?.externalParty?.fullName ||
    depositDetail?.customerBankAccount?.accountBankName ||
    movement?.otherUser?.name?.trim() ||
    movement?.otherPartyName?.trim() ||
    '';

  return {
    TRANSACTION_ID: String(depositDetail?.transactionId || movement.id),
    DATE_TRANSACTION: formattedDate,
    MONTO: currencyConfigFormat({ amount, currencyCode, hasCode: true }),
    COST_REP: costRep || computedCost,
    COMPANY_NAME: kycStatus?.company?.name || '',
    EMAIL: kycStatus?.b2bUser?.email || '',
    COUNTRY_REP:
      country?.nameES || kycStatus?.company?.addressCountry?.country || '',
    NAME_REMITTENCE: remitterName,
    BANK_REMITTENCE: bankTx?.bankName?.trim() || '',
    NUMBER_ACCUNT_REMITTENCE: bankTx?.accountNumber?.trim() || '',
    EMAIL_REMITTENCE: thirdParty?.email || '',
    DOCUMENT_TYPE_REMITTENCE: thirdParty?.documentType || '',
    DOCUMENT_NUMBER_REMITTENCE: thirdParty?.documentNumber || '',
  };
}

export function buildStablecoinVoucherValues({
  movement,
}: {
  movement: NewTypeMovement;
}): StablecoinVoucher {
  const senderWallet =
    movement?.otherPartyName || movement?.otherUser?.name || '';
  const currencyCode = movement?.currencyCode || movement?.originCurrency || '';

  return {
    TRANSACTION_ID: String(movement.id),
    DATE_TRANSACTION: formatMovementDate(movement?.date || 0),
    MONTO: currencyConfigFormat({
      amount: movement?.amount || 0,
      currencyCode,
      hasCode: true,
    }),
    STATUS: getStablecoinStatus(movement?.status).label,
    SENDER_WALLET: senderWallet,
  };
}

export function buildStablecoinPayoutVoucherValues({
  transactionData,
  movement,
}: {
  transactionData: IRemittanceDetailResponse;
  movement: NewTypeMovement;
}): StablecoinPayoutVoucher {
  const { beneficiary } = transactionData;
  const originCurrency = transactionData?.originCurrency || '';
  const destinyCurrency = transactionData?.destinyCurrency || originCurrency;

  const serviceFeeAmount =
    transactionData?.transactionCostList?.find(
      (cost) => cost.chargeType === 'COSTO OPERACIONAL'
    )?.amount || 0;

  const statusLabel = transferStatus(
    movement,
    transactionData?.txStatus?.statusDB
  );

  // El comprobante usa el truco `class="cell{{X}}"` para ocultar filas vacías;
  // un valor con espacio inicial generaría una clase `cell` suelta y ocultaría
  // la fila. Por eso los montos se entregan sin espacios al inicio/fin.
  const formatAmount = (amount: number, currencyCode: string): string =>
    currencyConfigFormat({ amount, currencyCode, hasCode: true }).trim();

  const amountSent = formatAmount(
    transactionData?.originAmount || 0,
    originCurrency
  );

  return {
    TRANSACTION_ID: String(transactionData?.transactionId ?? movement?.id ?? ''),
    DATE_TRANSACTION: formatMovementDate(
      transactionData?.startDateMillis || movement?.date || 0
    ),
    STATUS: statusLabel ? statusLabel.text : '',
    AMOUNT: amountSent,
    RECIPIENT: beneficiary?.name || '',
    EMAIL: beneficiary?.email || '',
    NETWORK: beneficiary?.accountBankName || '',
    WALLET_ADDRESS: beneficiary?.accountNumber || '',
    AMOUNT_SENT: amountSent,
    EXCHANGE_COST: formatAmount(transactionData?.fee || 0, originCurrency),
    SERVICE_FEE: formatAmount(serviceFeeAmount, originCurrency),
    AMOUNT_RECEIVED: formatAmount(
      transactionData?.destinyAmount || 0,
      destinyCurrency
    ),
    COMMENT: transactionData?.comment || '',
    PURPOSE: transactionData?.purpose || '',
  };
}

export function buildCurrencyCloudVoucherValues({
  movement,
  bankData,
  partnerMovement,
  countries,
  kycStatus,
}: {
  movement: NewTypeMovement;
  bankData: { bankName: string; bic?: string };
  partnerMovement?: NewTypeMovement;
  countries?: CountryData[];
  kycStatus?: KycCompliance;
}): CurrencyCloudVoucher {
  const formattedDate = formatMovementDate(movement?.date || 0);

  const country =
    countries?.find(
      (c: CountryData) =>
        c.alpha2Code === kycStatus?.company?.addressCountry?.country
    ) || ({} as CountryData);

  let receptionCost: string;
  if (partnerMovement?.amount && partnerMovement?.originCurrency) {
    receptionCost = currencyConfigFormat({
      amount: partnerMovement.amount,
      currencyCode: partnerMovement.originCurrency,
      hasCode: true,
    });
  } else {
    receptionCost = 'Sin costo';
  }

  return {
    TRANSACTION_ID: movement.id,
    DATE_TRANSACTION: formattedDate,
    MONTO: currencyConfigFormat({
      amount: movement.amount || 0,
      currencyCode: movement.originCurrency,
      hasCode: true,
    }),
    COST_REP: receptionCost,
    COMPANY_NAME: kycStatus?.company?.name || '',
    EMAIL: kycStatus?.b2bUser?.email || '',
    COUNTRY_REP:
      country?.nameES || kycStatus?.company?.addressCountry?.country || '',
    NAME_REMITTENCE: movement.otherUser?.name || '',
    BANK_REMITTENCE: bankData.bankName || '',
    NUMBER_ACCUNT_REMITTENCE: bankData.bic || '',
  };
}

export function buildExchangeVoucherValues({
  exchangeDetail,
  kycStatus,
}: {
  exchangeDetail: IExchangeDetail;
  kycStatus?: KycCompliance;
}): ExchangeVoucher {
  const isCOP =
    exchangeDetail.origin.currency === 'COP' ||
    exchangeDetail.destination.currency === 'COP';

  const isInternational =
    exchangeDetail.origin.currency !== exchangeDetail.destination.currency;

  const hasExchangeNumeral = Boolean(
    exchangeDetail.purposeOrigin || exchangeDetail.purposeDestiny
  );

  const showNumeralExchange = isInternational && isCOP && hasExchangeNumeral;

  const formattedDate = formatMovementDate(exchangeDetail?.createdDateMillis || 0);

  const originAmount = currencyConfigFormat({
    amount: exchangeDetail.origin.amount,
    currencyCode: exchangeDetail.origin.currency,
    hasCode: true,
  });

  const destinationAmount = currencyConfigFormat({
    amount: exchangeDetail.destination.amount,
    currencyCode: exchangeDetail.destination.currency,
    hasCode: true,
  });

  const feeAmount = currencyConfigFormat({
    amount: exchangeDetail.feeAmount,
    currencyCode: exchangeDetail.origin.currency,
    hasCode: true,
  });

  const exchangeRateCurrency =
    exchangeDetail.origin.amount < exchangeDetail.destination.amount
      ? exchangeDetail.destination.currency
      : exchangeDetail.origin.currency;

  const exchangeRateReferralCurrency =
    exchangeDetail.origin.amount < exchangeDetail.destination.amount
      ? exchangeDetail.origin.currency
      : exchangeDetail.destination.currency;

  const typeChange = currencyConfigFormat({
    amount: exchangeDetail.exchangeRate,
    currencyCode: exchangeRateCurrency,
    hasCode: true,
    decimalLimitDefault: 2,
  });
  const typeChangeLabel = `1 ${exchangeRateReferralCurrency} = ${typeChange}`;
  const rateBreakdown = getRateBreakdown({
    destinationAmount: exchangeDetail.destination.amount,
    destinationCurrency: exchangeDetail.destination.currency,
    fee: exchangeDetail.feeAmount,
    originAmount: exchangeDetail.origin.amount,
    originCurrency: exchangeDetail.origin.currency,
    rate: exchangeDetail.exchangeRate,
  });
  const safeGlobal66Label = rateBreakdown.showBreakdown
    ? rateBreakdown.global66Formatted
    : typeChangeLabel;

  const exchangeNumeral = showNumeralExchange
    ? exchangeDetail.purposeOrigin || exchangeDetail.purposeDestiny
    : '';

  function calculateTotalCost(): string {
    if (exchangeDetail.discountAmount) {
      const totalCost = Number(exchangeDetail.feeAmount - exchangeDetail.discountAmount);
      if (totalCost === 0) return 'Sin costo';
      return currencyConfigFormat({
        amount: totalCost,
        currencyCode: exchangeDetail.origin.currency,
        hasCode: true,
        decimalLimitDefault: 2,
      });
    }
    return feeAmount;
  }

  function calculateDiscount(): string {
    if (exchangeDetail.discountAmount) {
      return currencyConfigFormat({
        amount: exchangeDetail.discountAmount,
        currencyCode: exchangeDetail.origin.currency,
        hasCode: true,
        decimalLimitDefault: 2,
      });
    }
    return 'No aplica';
  }

  return {
    TRANSACTION_ID: exchangeDetail.transactionId,
    DATE_TRANSACTION: formattedDate,
    NAME_CUSTOMER: kycStatus?.company?.name || '',
    EMAIL: kycStatus?.b2bUser?.email || '',
    ORIGIN_AMOUNT: originAmount,
    AMOUNT_DESTINATION: destinationAmount,
    EXCHANGE_RATE_COST: feeAmount,
    TYPE_CHANGE: typeChangeLabel,
    GLOBAL66_TYPE_CHANGE: safeGlobal66Label,
    SPOT_RATE: rateBreakdown.showBreakdown
      ? rateBreakdown.spotFormatted || undefined
      : undefined,
    SPREAD_RATE: rateBreakdown.showBreakdown
      ? rateBreakdown.spreadFormatted || undefined
      : undefined,
    EXCHANGE_NUMERAL: exchangeNumeral,
    DISCOUNT_AMOUNT: calculateDiscount(),
    DISCOUNT_CLASS: exchangeDetail.discountAmount ? 'text-green-2' : 'text-gray-4',
    TOTAL_COST: calculateTotalCost(),
  };
}

/**
 * Valores del comprobante de un movimiento P2P. En un envío entre cuentas de la misma
 * moneda el backend no manda tasa —`exchangeRate` llega en 0— y la conversión es 1 a 1,
 * así que la fila de tipo de cambio usa 1 y no el 0 que dejaba `1 USD = $ 0.00 USD`.
 * @param params - Movimiento, detalle P2P, países y datos de KYC del titular.
 * @returns Los valores que la plantilla del comprobante P2P interpola.
 */
export function buildP2PVoucherValues({
  movement,
  p2pDetail,
  countries,
  kycStatus,
  hideComment = false,
  useMovementRemitterNameForReceived = false,
}: {
  movement: NewTypeMovement;
  p2pDetail: {
    initiator?: {
      accountGroup?: { currencyCode: string };
      customer?: { firstName: string; lastName: string; email: string };
    } | null;
    receiver?: {
      accountGroup?: { currencyCode: string };
      customer?: { firstName: string; lastName: string; email: string; countryCode: string };
    } | null;
    purposeOriginNumeral: string;
    purposeDestinyNumeral: string;
    createdDateMillis: number;
    originAmount: number;
    destinationAmount: number;
    quote?: { fee: number } | null;
    exchangeRate: number;
    phoneNumberReceiver: string;
    p2pTransactionId: string | number;
  };
  countries?: CountryData[];
  kycStatus?: KycCompliance;
  hideComment?: boolean;
  /** Solo cuentas virtuales (flag ON): en recepción usa el nombre del movimiento como remitente. */
  useMovementRemitterNameForReceived?: boolean;
}): P2pVoucher {
  const originCurrency = p2pDetail.initiator?.accountGroup?.currencyCode ?? '';
  const destinationCurrency = p2pDetail.receiver?.accountGroup?.currencyCode ?? '';

  const isCOP = originCurrency === 'COP' || destinationCurrency === 'COP';
  const isInternational = originCurrency !== destinationCurrency;
  const hasExchangeNumeral = Boolean(
    p2pDetail.purposeOriginNumeral || p2pDetail.purposeDestinyNumeral
  );
  const showNumeralExchange = isInternational && isCOP && hasExchangeNumeral;
  const exchangeNumeral = showNumeralExchange
    ? p2pDetail.purposeOriginNumeral || p2pDetail.purposeDestinyNumeral
    : '';

  const formattedDate = formatMovementDate(p2pDetail?.createdDateMillis || 0);

  const originAmount = currencyConfigFormat({
    amount: p2pDetail.originAmount,
    currencyCode: originCurrency,
    hasCode: true,
  });

  const destinationAmount = currencyConfigFormat({
    amount: p2pDetail.destinationAmount,
    currencyCode: destinationCurrency,
    hasCode: true,
  });

  const fee = p2pDetail.quote?.fee ?? 0;
  const hasSameCurrency =
    Boolean(originCurrency) && originCurrency === destinationCurrency;
  const exchangeRate = hasSameCurrency ? 1 : p2pDetail.exchangeRate ?? 0;

  const feeAmount = currencyConfigFormat({
    amount: fee,
    currencyCode: originCurrency,
    hasCode: true,
  });

  const exchangeRateCurrency =
    p2pDetail.originAmount < p2pDetail.destinationAmount
      ? destinationCurrency
      : originCurrency;

  const exchangeRateReferralCurrency =
    p2pDetail.originAmount < p2pDetail.destinationAmount
      ? originCurrency
      : destinationCurrency;

  const typeChange = currencyConfigFormat({
    amount: exchangeRate,
    currencyCode: exchangeRateCurrency,
    hasCode: true,
    decimalLimitDefault: 2,
  });
  const typeChangeLabel = `1 ${exchangeRateReferralCurrency} = ${typeChange}`;
  const rateBreakdown = getRateBreakdown({
    destinationAmount: p2pDetail.destinationAmount,
    destinationCurrency,
    fee,
    originAmount: p2pDetail.originAmount,
    originCurrency,
    rate: exchangeRate,
  });
  const safeGlobal66Label = rateBreakdown.showBreakdown
    ? rateBreakdown.global66Formatted
    : typeChangeLabel;

  const { firstName: initiatorName = '', lastName: initiatorLastName = '' } =
    p2pDetail.initiator?.customer ?? {};
  const {
    firstName: receiverName = '',
    lastName: receiverLastName = '',
    countryCode: receiverCountryCode = '',
  } = p2pDetail.receiver?.customer ?? {};

  const userPhoneNumber = `(${kycStatus?.b2bUser.phoneCountryCode}) ${kycStatus?.b2bUser.phoneNumber}`;
  const phoneToShow = movement.type.includes('SENT')
    ? userPhoneNumber
    : p2pDetail.phoneNumberReceiver;

  const receiverCountry =
    countries?.find((c: CountryData) => c.alpha2Code === receiverCountryCode) ||
    ({} as CountryData);

  const transactionType = movement.type.includes('SENT') ? 'envío a' : 'recepción en';
  const global66TypeChange = safeGlobal66Label || typeChangeLabel;

  const isSent = movement.type.includes('SENT');
  const initiatorFullName = `${initiatorName} ${initiatorLastName || ''}`.trim();
  const receiverFullName = `${receiverName} ${receiverLastName || ''}`.trim();
  const otherPartyDisplayName =
    movement.otherUser?.name?.trim() || movement.otherPartyName?.trim() || '';

  const nameCustomer =
    useMovementRemitterNameForReceived && !isSent
      ? otherPartyDisplayName || initiatorFullName
      : initiatorFullName;
  const nameRecipient = receiverFullName;

  return {
    TRANSACTION_TYPE: transactionType,
    TRANSACTION_ID: p2pDetail.p2pTransactionId,
    DATE_TRANSACTION: formattedDate,
    NAME_CUSTOMER: nameCustomer,
    EMAIL_CUSTOMER: '',
    TELEPHONE: phoneToShow || '',
    NAME_RECIPIENT: nameRecipient,
    EMAIL_RECIPIENT: '',
    COUNTRY_RECIPIENT: hideComment ? '' : receiverCountry.nameES || '',
    AMOUNT_SEND: originAmount.trim(),
    AMOUNT_RECEIVED: destinationAmount.trim(),
    CONST_CHANGE_TOTAL: feeAmount.trim(),
    TYPE_CHANGE: typeChangeLabel,
    GLOBAL66_TYPE_CHANGE: global66TypeChange,
    SPOT_RATE: rateBreakdown.showBreakdown
      ? rateBreakdown.spotFormatted || undefined
      : undefined,
    SPREAD_RATE: rateBreakdown.showBreakdown
      ? rateBreakdown.spreadFormatted || undefined
      : undefined,
    EXCHANGE_NUMERAL: exchangeNumeral,
    COMMENT: hideComment ? '' : movement.comment || '-',
  };
}

export function buildPseVoucherValues({
  movement,
}: {
  movement: NewTypeMovement;
}): PseVoucher {
  function getTextTx() {
    return movement?.status === 'PAID'
      ? 'Transacción exitosa'
      : movement?.status === 'PENDING'
        ? 'Transacción pendiente'
        : 'Transacción rechazada';
  }

  const formattedDate = formatMovementDate(movement.date || 0);

  const transactionValue = currencyConfigFormat({
    amount: movement.amount,
    currencyCode: movement.originCurrency,
    hasCode: true,
  });

  return {
    paymentStatus: getTextTx(),
    invoiceNumber: movement.pseDetail?.invoiceNumber ?? '',
    transactionValue,
    entityName: movement.pseDetail?.entityName ?? '',
    paymentDescription: movement.pseDetail?.paymentDescription ?? '',
    reference1: movement.pseDetail?.referenceNumber1 ?? '',
    reference2: movement.pseDetail?.referenceNumber2 ?? '',
    reference3: movement.pseDetail?.referenceNumber3 ?? '',
    refPaymentCus: movement.pseDetail?.trazabilityCode ?? '',
    vatValue: movement.pseDetail?.vatValue ?? '',
    paymentDate: formattedDate,
    accountType: movement.pseDetail?.accountType ?? '',
    accountNumber: movement.pseDetail?.accountNumber ?? '',
    transactionId: movement.id,
    imageTx: '',
    errorDetail: '',
    fraudMessage: '',
  };
}
